import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Calendar, Package, Users, Clock, BarChart3, Activity,
  Share2, MessageSquare, LayoutDashboard, CalendarDays,
  Settings, ChevronDown, ChevronUp, Loader2,
  ArrowRight, CheckCircle2, Dumbbell, Apple, ClipboardList, Play, Pause,
  Sparkles, Shield, Zap, Target, Menu, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_ICONS = {
  dashboard: LayoutDashboard, calendar: Calendar, weekly: CalendarDays,
  inventory: Package, workers: Users, "shift-types": Clock,
  "training-plans": Dumbbell, nutrition: Apple, anamnese: ClipboardList,
  bioimpedance: Activity, "social-media": Share2,
  "ai-chat": MessageSquare, statistics: BarChart3, integrations: Settings, crm: Users,
};
const FEATURE_KEYS = {
  dashboard: "nav_dashboard", calendar: "nav_calendar", weekly: "nav_weekly",
  inventory: "nav_inventory", workers: "nav_workers", "shift-types": "nav_shift_types",
  "training-plans": "nav_training_plans", nutrition: "nav_nutrition", anamnese: "nav_anamnese",
  bioimpedance: "nav_bioimpedance", "social-media": "nav_social_media",
  "ai-chat": "nav_ai_chat", statistics: "nav_statistics", integrations: "nav_integrations", crm: "nav_crm",
};
const BASIC_FEATURES = ["dashboard", "calendar", "weekly", "inventory", "workers", "shift-types", "training-plans"];
const PREMIUM_FEATURES = [...BASIC_FEATURES, "nutrition", "anamnese", "bioimpedance", "social-media", "ai-chat", "statistics", "integrations", "crm"];

const PREVIEWS = {
  nutrition: "/preview_nutrition.jpeg", anamnese: "/preview_anamnese.jpeg",
  training: "/preview_training.jpeg", dashboard: "/preview_dashboard.jpeg",
  crm: "/preview_crm.jpeg", revenue: "/preview_revenue.jpeg",
  weekly: "/preview_weekly.jpeg", calendar: "/preview_calendar.jpeg",
  bioimpedance: "/preview_bio.jpeg",
};

// Animated section wrapper
function AnimSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-5 text-left group" data-testid="faq-toggle">
        <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">{q}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }} className="overflow-hidden">
        <p className="text-sm text-zinc-400 pb-5 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}

export default function LandingPage() {
  const { t, lang: globalLang, setLang: setGlobalLang } = useLanguage();
  const [lang, setLangLocal] = useState(globalLang || "de");

  const setLang = (l) => { setLangLocal(l); setGlobalLang(l); };
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const videoRef = useRef(null);

  const tx = (de, en, pt) => lang === "en" ? en : lang === "pt" ? pt : de;

  const handleAuth = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const endpoint = isLogin ? "/api/auth/login" : "/api/auth/register";
      const res = await axios.post(`${API}${endpoint}`, { email, password });
      localStorage.setItem("token", res.data.token);
      window.location.href = "/";
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
    setLoading(false);
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoPlaying) videoRef.current.pause(); else videoRef.current.play();
      setVideoPlaying(!videoPlaying);
    }
  };

  const benefits = [
    { icon: Zap, title: tx("Zeitersparnis", "Time Savings", "Economia de Tempo"), desc: tx("Bis zu 10 Stunden pro Woche weniger Verwaltung", "Up to 10 hours less admin per week", "Até 10 horas a menos de administração por semana") },
    { icon: Target, title: tx("Kundenbindung", "Client Retention", "Retenção de Clientes"), desc: tx("Personalisierte Pläne steigern die Zufriedenheit", "Personalized plans boost satisfaction", "Planos personalizados aumentam a satisfação") },
    { icon: Sparkles, title: tx("KI-Automatisierung", "AI Automation", "Automação por IA"), desc: tx("Ernährungs- und Trainingspläne per Knopfdruck", "Nutrition and training plans at the push of a button", "Planos nutricionais e de treino com um clique") },
    { icon: Shield, title: tx("Alles an einem Ort", "All in One Place", "Tudo em Um Só Lugar"), desc: tx("Schichten, Kunden, Pläne — eine Plattform", "Shifts, clients, plans — one platform", "Turnos, clientes, planos — uma plataforma") },
  ];

  const featureCards = [
    { key: "nutrition", title: tx("KI-Ernährungsplan", "AI Nutrition Plan", "Plano Nutricional IA"), desc: tx("TDEE-Berechnung, 3 Optionen pro Mahlzeit, PDF-Export in DE/PT/EN", "TDEE calculation, 3 options per meal, PDF export DE/PT/EN", "Cálculo TDEE, 3 opções por refeição, PDF DE/PT/EN"), img: PREVIEWS.nutrition, span: "md:col-span-8 row-span-2" },
    { key: "training", title: tx("KI-Trainingsplaner", "AI Training Planner", "Planejador de Treino IA"), desc: tx("279 Übungen, PDF mit Bildern, Templates, Muskelgruppen-Filter", "279 exercises, PDF with images, templates, muscle group filters", "279 exercícios, PDF com imagens, templates, filtros por grupo muscular"), img: PREVIEWS.training, span: "md:col-span-4 row-span-2" },
    { key: "bioimpedance", title: tx("Bioimpedanz-Analyse", "Bioimpedance Analysis", "Análise de Bioimpedância"), desc: tx("Körperzusammensetzung, Gewichtsverlauf, persönliche Ziele", "Body composition, weight tracking, personal goals", "Composição corporal, acompanhamento de peso, metas pessoais"), img: PREVIEWS.bioimpedance, span: "md:col-span-4" },
    { key: "anamnese", title: tx("Digitale Anamnese", "Digital Anamnesis", "Anamnese Digital"), desc: tx("31 Fragen, KI liest ausgefüllte PDFs, automatische Plan-Erstellung", "31 questions, AI reads filled PDFs, automatic plan creation", "31 perguntas, IA lê PDFs preenchidos, criação automática de plano"), img: PREVIEWS.anamnese, span: "md:col-span-4" },
    { key: "calendar", title: tx("Schichtplanung", "Shift Planning", "Planejamento de Turnos"), desc: tx("Drag & Drop Kalender, Wochenansicht, Mitarbeiterverwaltung", "Drag & drop calendar, weekly view, employee management", "Calendário drag & drop, visão semanal, gestão de funcionários"), img: PREVIEWS.calendar, span: "md:col-span-4" },
    { key: "crm", title: tx("Kunden-CRM", "Client CRM", "CRM de Clientes"), desc: tx("Kundenverwaltung, Kontakthistorie, Bioimpedanz-Integration", "Client management, contact history, bioimpedance integration", "Gestão de clientes, histórico de contato, integração bioimpedância"), img: PREVIEWS.crm, span: "md:col-span-4" },
    { key: "dashboard", title: tx("Dashboard & Statistiken", "Dashboard & Statistics", "Dashboard & Estatísticas"), desc: tx("Tagesübersicht, To-Dos, Erinnerungen, Notizen", "Daily overview, to-dos, reminders, notes", "Visão diária, tarefas, lembretes, notas"), img: PREVIEWS.dashboard, span: "md:col-span-4" },
  ];

  const faqs = [
    { q: tx("Wie bekomme ich Zugang zu Athlo Nexus?", "How do I get access to Athlo Nexus?", "Como obtenho acesso ao Athlo Nexus?"), a: tx("Wählen Sie einen unserer Pläne (Basic oder Premium) und registrieren Sie sich. Nach der Zahlung erhalten Sie sofortigen Zugang zu allen Funktionen Ihres Pakets.", "Choose one of our plans (Basic or Premium) and register. After payment, you get instant access to all features in your package.", "Escolha um dos nossos planos (Basic ou Premium) e registre-se. Após o pagamento, você terá acesso imediato a todas as funcionalidades do seu pacote.") },
    { q: tx("Welche Zahlungsmethoden werden akzeptiert?", "What payment methods are accepted?", "Quais métodos de pagamento são aceitos?"), a: tx("Wir akzeptieren alle gängigen Kreditkarten über Stripe.", "We accept all major credit cards via Stripe.", "Aceitamos todos os cartões de crédito principais via Stripe.") },
    { q: tx("Ist meine Daten sicher?", "Is my data safe?", "Meus dados estão seguros?"), a: tx("Ja, alle Daten werden verschlüsselt gespeichert und regelmäßig gesichert.", "Yes, all data is encrypted and regularly backed up.", "Sim, todos os dados são criptografados e têm backup regular.") },
    { q: tx("Kann ich jederzeit kündigen?", "Can I cancel anytime?", "Posso cancelar a qualquer momento?"), a: tx("Ja, Sie können Ihr Abonnement jederzeit ohne Kündigungsfrist beenden.", "Yes, you can end your subscription at any time without notice.", "Sim, você pode encerrar sua assinatura a qualquer momento sem aviso prévio.") },
  ];

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-white overflow-x-hidden" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#1A1A1A]/80 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-athlo-nexus.png" alt="Athlo Nexus" className="w-8 h-8 rounded-lg" />
            <span className="text-base font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Athlo Nexus</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-xs text-zinc-400 hover:text-white transition-colors font-medium">{tx("Funktionen", "Features", "Funcionalidades")}</a>
            <a href="#pricing" className="text-xs text-zinc-400 hover:text-white transition-colors font-medium">{tx("Preise", "Pricing", "Preços")}</a>
            <a href="#faq" className="text-xs text-zinc-400 hover:text-white transition-colors font-medium">FAQ</a>
            <a href="#support" className="text-xs text-zinc-400 hover:text-white transition-colors font-medium">{tx("Support", "Support", "Suporte")}</a>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:flex bg-white/[0.06] rounded-full p-0.5" data-testid="lang-selector">
              {["de","en","pt"].map(l => (
                <button key={l} onClick={() => setLang(l)} className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition-colors ${lang === l ? "bg-white/15 text-white" : "text-zinc-500 hover:text-zinc-300"}`} data-testid={`lang-${l}`}>{l}</button>
              ))}
            </div>
            <Button onClick={() => { setShowAuth(true); setIsLogin(true); }} size="sm"
              className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-full px-5 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(0,188,212,0.4)] transition-all"
              data-testid="get-started-btn">
              Login
            </Button>
            <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="mobile-menu-btn">
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown */}
        {mobileMenu && (
          <div className="md:hidden border-t border-white/[0.06] bg-[#1A1A1A]/95 backdrop-blur-xl" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-3">
              <a href="#features" onClick={() => setMobileMenu(false)} className="block text-sm text-zinc-300 hover:text-white py-1.5 transition-colors">{tx("Funktionen", "Features", "Funcionalidades")}</a>
              <a href="#pricing" onClick={() => setMobileMenu(false)} className="block text-sm text-zinc-300 hover:text-white py-1.5 transition-colors">{tx("Preise", "Pricing", "Preços")}</a>
              <a href="#faq" onClick={() => setMobileMenu(false)} className="block text-sm text-zinc-300 hover:text-white py-1.5 transition-colors">FAQ</a>
              <a href="#support" onClick={() => setMobileMenu(false)} className="block text-sm text-zinc-300 hover:text-white py-1.5 transition-colors">{tx("Support", "Support", "Suporte")}</a>
              <div className="flex gap-2 pt-2 border-t border-white/[0.06]">
                {["de","en","pt"].map(l => (
                  <button key={l} onClick={() => { setLang(l); setMobileMenu(false); }} className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase ${lang === l ? "bg-white/15 text-white" : "text-zinc-500"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="min-h-screen flex flex-col items-center justify-center pt-16 px-6 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-cyan-500/[0.04] blur-[120px]" />
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="mb-8" data-testid="hero-logo">
            <h2 className="text-6xl sm:text-7xl lg:text-8xl font-black tracking-tighter text-white leading-none" style={{ fontFamily: "Chivo, sans-serif" }}>
              ATHLO<br />NEXUS
            </h2>
            <p className="text-sm sm:text-base font-bold uppercase tracking-[0.3em] mt-3 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-emerald-400">
              Wissen trifft Leistung
            </p>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}
            className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight mb-5" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Die All-in-One Plattform für", "The All-in-One Platform for", "A Plataforma Completa para")}{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-emerald-400">
              {tx("Dein Studio", "Your Studio", "Seu Estúdio")}
            </span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="text-sm sm:text-base text-zinc-500 max-w-xl mx-auto mb-10 leading-relaxed">
            {tx("Schichten, Trainingspläne, Ernährung, CRM und KI-Automatisierung — alles in einer Plattform. Weniger Verwaltung, mehr Training.",
              "Shifts, training plans, nutrition, CRM and AI automation — all in one platform. Less admin, more training.",
              "Turnos, planos de treino, nutrição, CRM e automação por IA — tudo numa plataforma. Menos administração, mais treino.")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => { setShowAuth(true); setIsLogin(false); }} size="lg"
              className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-full px-8 py-3 text-sm hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(0,188,212,0.4)] transition-all"
              data-testid="hero-signup-btn">
              {tx("Jetzt starten", "Get Started", "Começar Agora")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="ghost" size="lg" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
              className="text-zinc-400 hover:text-white rounded-full px-8 border border-white/10 hover:border-white/20" data-testid="hero-features-btn">
              {tx("Funktionen entdecken", "Explore Features", "Explorar Funcionalidades")}
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Demo Video */}
      <section className="px-6 pb-24 -mt-8 relative z-10">
        <AnimSection className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 relative group">
            <video ref={videoRef} src="/demo.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-testid="hero-demo-video" poster={PREVIEWS.weekly} />
            <button onClick={toggleVideo} className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="video-toggle">
              {videoPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
            </button>
          </div>
        </AnimSection>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6">
        <AnimSection className="max-w-6xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400 mb-3">{tx("Warum Athlo Nexus", "Why Athlo Nexus", "Por que Athlo Nexus")}</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-14" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Ihr Studio. Vollständig digitalisiert.", "Your Studio. Fully Digitized.", "Seu Estúdio. Totalmente Digitalizado.")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <AnimSection key={i} delay={i * 0.1}>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/10 transition-all group" data-testid={`benefit-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-emerald-500/30 transition-colors">
                    <b.icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1.5">{b.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{b.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
        </AnimSection>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-24 px-6">
        <AnimSection className="max-w-6xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3">{tx("Funktionen", "Features", "Funcionalidades")}</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-14" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Alles, was Ihr Studio braucht", "Everything Your Studio Needs", "Tudo que Seu Estúdio Precisa")}
          </h2>
          <div className="grid grid-cols-12 gap-4">
            {featureCards.map((f, i) => (
              <AnimSection key={f.key} delay={i * 0.08} className={`col-span-12 ${f.span} group`}>
                <div className="h-full bg-white/[0.03] border border-white/[0.06] rounded-2xl overflow-hidden hover:border-white/10 transition-all">
                  {f.img && (
                    <div className="overflow-hidden">
                      <img src={f.img} alt={f.title} className="w-full h-auto opacity-70 group-hover:opacity-90 group-hover:scale-[1.02] transition-all duration-500" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="text-sm font-bold text-white mb-1">{f.title}</h3>
                    <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </AnimSection>
            ))}
          </div>
        </AnimSection>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6">
        <AnimSection className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400 mb-3">{tx("Preise", "Pricing", "Preços")}</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-14" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Einfache, transparente Preise", "Simple, Transparent Pricing", "Preços Simples e Transparentes")}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            {/* Basic */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7">
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1">Basic</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-4xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>€14</span>
                <span className="text-sm text-zinc-500">/{tx("Monat", "month", "mês")}</span>
              </div>
              <ul className="space-y-2.5 mb-7">
                {BASIC_FEATURES.map(f => {
                  const Icon = FEATURE_ICONS[f] || CheckCircle2;
                  return (
                    <li key={f} className="flex items-center gap-2.5 text-xs text-zinc-400">
                      <Icon className="w-3.5 h-3.5 text-zinc-600 flex-shrink-0" /> {t(FEATURE_KEYS[f])}
                    </li>
                  );
                })}
              </ul>
              <Button variant="outline" className="w-full rounded-full border-white/10 text-white hover:bg-white/5" onClick={() => { setShowAuth(true); setIsLogin(true); }}
                data-testid="basic-plan-btn">{tx("Basic wählen", "Choose Basic", "Escolher Basic")}</Button>
            </div>
            {/* Premium */}
            <div className="bg-gradient-to-b from-cyan-500/[0.06] to-emerald-500/[0.03] border border-cyan-500/20 rounded-2xl p-7 relative shadow-[0_0_40px_rgba(0,188,212,0.08)]">
              <div className="absolute -top-3 right-6 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                {tx("Empfohlen", "Recommended", "Recomendado")}
              </div>
              <p className="text-xs font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-emerald-400 uppercase tracking-wider mb-1">Premium</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-4xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>€19</span>
                <span className="text-sm text-zinc-500">/{tx("Monat", "month", "mês")}</span>
              </div>
              <ul className="space-y-2.5 mb-7">
                {PREMIUM_FEATURES.map(f => {
                  const Icon = FEATURE_ICONS[f] || CheckCircle2;
                  const isPremiumOnly = !BASIC_FEATURES.includes(f);
                  return (
                    <li key={f} className="flex items-center gap-2.5 text-xs">
                      <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isPremiumOnly ? "text-cyan-400" : "text-zinc-600"}`} />
                      <span className={isPremiumOnly ? "text-white font-semibold" : "text-zinc-400"}>{t(FEATURE_KEYS[f])}</span>
                      {isPremiumOnly && <span className="text-[9px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded-full font-bold">Premium</span>}
                    </li>
                  );
                })}
              </ul>
              <Button className="w-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(0,188,212,0.4)] transition-all"
                onClick={() => { setShowAuth(true); setIsLogin(true); }} data-testid="premium-plan-btn">
                {tx("Premium starten", "Start Premium", "Começar Premium")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </AnimSection>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6">
        <AnimSection className="max-w-3xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3 text-center">FAQ</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-12 text-center" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Häufig gestellte Fragen", "Frequently Asked Questions", "Perguntas Frequentes")}
          </h2>
          <div className="border-t border-white/10">
            {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
        </AnimSection>
      </section>

      {/* Support */}
      <section id="support" className="py-24 px-6 border-t border-white/[0.06]">
        <AnimSection className="max-w-xl mx-auto text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">{tx("Support", "Support", "Suporte")}</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>
            {tx("Wie können wir helfen?", "How can we help?", "Como podemos ajudar?")}
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            {tx("Haben Sie Fragen oder Probleme? Schreiben Sie uns — wir antworten so schnell wie möglich.",
              "Have questions or issues? Write to us — we'll reply as soon as possible.",
              "Tem perguntas ou problemas? Escreva para nós — responderemos o mais rápido possível.")}
          </p>
          <form onSubmit={(e) => {
            e.preventDefault();
            const form = e.target;
            const name = form.elements['support-name'].value;
            const email = form.elements['support-email'].value;
            const message = form.elements['support-message'].value;
            window.location.href = `mailto:athlonexus@gmail.com?subject=${encodeURIComponent(`Support: ${name}`)}&body=${encodeURIComponent(`Von: ${name}\nE-Mail: ${email}\n\n${message}`)}`;
            form.reset();
          }} className="text-left space-y-3" data-testid="support-form">
            <div className="grid grid-cols-2 gap-3">
              <input name="support-name" required placeholder={tx("Name", "Name", "Nome")}
                className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50" data-testid="support-name" />
              <input name="support-email" type="email" required placeholder="E-Mail"
                className="bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50" data-testid="support-email" />
            </div>
            <textarea name="support-message" required rows={4} placeholder={tx("Ihre Nachricht...", "Your message...", "Sua mensagem...")}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50 resize-none" data-testid="support-message" />
            <Button type="submit"
              className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-full py-2.5 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(0,188,212,0.4)] transition-all"
              data-testid="support-submit">
              {tx("Nachricht senden", "Send Message", "Enviar Mensagem")}
            </Button>
          </form>
          <p className="text-[11px] text-zinc-600 mt-4">{tx("Oder schreiben Sie direkt an", "Or write directly to", "Ou escreva diretamente para")} <a href="mailto:athlonexus@gmail.com" className="text-cyan-400 hover:underline">athlonexus@gmail.com</a></p>
        </AnimSection>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-10 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-athlo-nexus.png" alt="Athlo Nexus" className="w-6 h-6 rounded" />
            <span className="text-xs font-semibold text-zinc-500">Athlo Nexus</span>
          </div>
          <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} Athlo Nexus. {tx("Alle Rechte vorbehalten.", "All rights reserved.", "Todos os direitos reservados.")}</p>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowAuth(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
            className="bg-[#232323] border border-white/10 rounded-2xl p-8 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()} data-testid="auth-modal">
            <div className="flex items-center gap-2 mb-6">
              <img src="/logo-athlo-nexus.png" alt="" className="w-7 h-7 rounded-lg" />
              <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Athlo Nexus</span>
            </div>
            <div className="flex mb-5 bg-white/[0.04] rounded-full p-0.5">
              <button onClick={() => setIsLogin(true)} className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${isLogin ? "bg-white/10 text-white" : "text-zinc-500"}`} data-testid="auth-login-tab">Login</button>
              <button onClick={() => setIsLogin(false)} className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${!isLogin ? "bg-white/10 text-white" : "text-zinc-500"}`} data-testid="auth-register-tab">{tx("Registrieren", "Register", "Registrar")}</button>
            </div>
            <form onSubmit={handleAuth} className="space-y-3">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-Mail" required
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50" data-testid="auth-email" />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={tx("Passwort", "Password", "Senha")} required
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-cyan-500/50" data-testid="auth-password" />
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-full py-2.5 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(0,188,212,0.4)] transition-all"
                data-testid="auth-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? "Login" : tx("Registrieren", "Register", "Registrar")}
              </Button>
            </form>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center"><span className="bg-[#232323] px-3 text-[10px] text-zinc-500 uppercase tracking-wider">{tx("oder", "or", "ou")}</span></div>
            </div>
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button onClick={() => {
              const redirectUrl = window.location.origin + '/';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
              className="w-full flex items-center justify-center gap-2.5 bg-white/[0.06] border border-white/10 rounded-full py-2.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="google-signin-btn">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {tx("Mit Google anmelden", "Sign in with Google", "Entrar com Google")}
            </button>
          </motion.div>
        </div>
      )}

      {/* Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Chivo:wght@700;900&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
    </div>
  );
}
